import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { SalesDataTimePeriod } from "../../data-store";
import useSalesData from "../../data-store/useSalesData";
import useUserInfo from "../../data-store/useUserInfo";
import { effectTypes } from "../../domain/EffectType";
import { ImpactCategory } from "../../domain/impactCategories";
import { ImpactRating, allImpactRatings } from "../../domain/impactRatings";
import { IntegrationType } from "../../domain/integrationType";
import useAvailableImpactCategories from "../../services/useOrganizationFeatures";
import * as comparators from "../../util/comparators";
import { convertFiltersToQueryParams } from "../../util/filters";
import { formatTimePeriod } from "../../util/timePeriodFormatting";
import Label from "../labels/Label";
import Page from "../Page";
import { RecipesListPageQueryParams, usePages } from "../pages";
import ProductFilterSelect from "../product-filter/ProductFilterSelect";
import ImpactCategoryToggle from "../recipes/ImpactCategoryToggle";
import {
  ImpactCategoryProvider,
  useImpactCategory,
} from "../sales-dashboard/ImpactCategoryProvider";
import TimePeriodSelector from "../sales-dashboard/TimePeriodSelector";
import { formatString, intlFormatNumber } from "../scope-3/helperFunctions";
import StatusDisplay from "../StatusDisplay";
import { SecondaryButtonLink } from "../utils/Button";
import { Panel } from "../utils/Panel";
import SearchBox from "../utils/SearchBox";
import { Table, TableColumn, TableSort } from "../utils/Table";
import useStateWithDebouncedUpdate from "../utils/useStateWithDebouncedUpdate";
import { Upload } from "../utils/Vectors";

import "../Page.css";
import "./SalesDataPage.css";

const tracking = { pageName: "salesData" };

interface TimePeriodDisplayProps {
  selectedTimePeriod: SalesDataTimePeriod | null;
}

function TimePeriodDisplay({ selectedTimePeriod }: TimePeriodDisplayProps) {
  const intl = useIntl();

  if (!selectedTimePeriod) {
    return null;
  }

  const formattedPeriod = formatTimePeriod(selectedTimePeriod, intl);

  return <div className="SalesDataTimePeriod">{formattedPeriod}</div>;
}

export interface SalesDataItem {
  id: number;
  code: string | null;
  name: string;
  mass: number;
  massUnit: string[];
  sales: number;
  ghgEmissions: number;
  ghgEmissionsUnit: [string, string];
  ghgContribution: number;
  ghgRating: ImpactRating;
  ghgIntensity: number;
  landUse: number;
  landUseContribution: number;
  landUseRating: ImpactRating;
  landUseIntensity: number;
  waterUse: number;
  waterUseContribution: number;
  waterUseRating: ImpactRating;
  waterUseIntensity: number;
  tags: string[];
  latestUpload: string;
}

const convertFiltersAndSearchTermToQueryParams = (
  queryParams: RecipesListPageQueryParams
) => {
  const params = new URLSearchParams(convertFiltersToQueryParams(queryParams));
  if (queryParams.searchTerm) {
    params.set("searchTerm", queryParams.searchTerm);
  }
  return params.toString();
};

export default function SalesDataPage() {
  const pages = usePages();
  const [queryParams, setQueryParams] = pages.RecipesListPage.useQueryParams();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<SalesDataTimePeriod | null>(null);

  const combinedParams = convertFiltersAndSearchTermToQueryParams(queryParams);

  const onChange = (input: Partial<RecipesListPageQueryParams>) => {
    setQueryParams(input);
  };

  const handleTimePeriodChange = (period: SalesDataTimePeriod | null) => {
    setSelectedTimePeriod(period);
  };

  return (
    <Page contentHasPadding={false} tracking={tracking}>
      <ImpactCategoryProvider>
        <div className="StickyPageTitle SalesDataPage_Header">
          <Page.Title
            breadcrumb={pages.SalesData.breadcrumb()}
            title={pages.SalesData.title}
            rightAlignEndComponent={true}
          />
          <div
            className="d-flex justify-content-between w-100"
            style={{ padding: "16px 0 0 0" }}
          >
            <div className="d-flex flex-column" style={{ gap: 16 }}>
              <div>
                <SalesDataPageControlsLeft
                  onChange={onChange}
                  queryParams={queryParams}
                  menuIsOpen={menuIsOpen}
                  setMenuIsOpen={setMenuIsOpen}
                />
              </div>
              <div style={{ padding: "9px 0 0 0" }}>
                {/* 8 (padding of button) + 1 (border of button) to make in line with impact toggle */}
                <TimePeriodDisplay selectedTimePeriod={selectedTimePeriod} />
              </div>
            </div>
            <div
              className="d-flex flex-column align-items-end"
              style={{ gap: 16 }}
            >
              <SalesDataPageControlsRight />
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 0 0 24px" }}>
          <TimePeriodSelector
            selectedPeriod={selectedTimePeriod}
            onPeriodChange={handleTimePeriodChange}
          />
        </div>
        <div className="SalesDataPageContent">
          <div className="SalesDataTableContainer">
            <div className="sales-data-table">
              <Panel className="RecipesTable_Panel">
                <SalesDataTableWithStatus
                  combinedParams={combinedParams}
                  selectedTimePeriod={selectedTimePeriod}
                />
              </Panel>
            </div>
          </div>
        </div>
      </ImpactCategoryProvider>
    </Page>
  );
}

interface SalesDataPageControlsLeftProps {
  onChange: (value: Partial<RecipesListPageQueryParams>) => void;
  queryParams: RecipesListPageQueryParams;
  menuIsOpen: boolean;
  setMenuIsOpen: (menuIsOpen: boolean) => void;
}

export function SalesDataPageControlsLeft(
  props: SalesDataPageControlsLeftProps
) {
  const { onChange, queryParams, menuIsOpen, setMenuIsOpen } = props;
  const intl = useIntl();

  const [searchTerm, setSearchTerm] = useStateWithDebouncedUpdate({
    initialValue: queryParams.searchTerm || "",
    update: (newValue: string) => onChange({ searchTerm: newValue }),
  });

  const searchPlaceholder = intl.formatMessage({
    id: "components/sales-data/SalesDataPage:searchPlaceholder",
    defaultMessage: "Search products",
  });

  return (
    <div className="SalesDataPageControlsLeft">
      <div className="SalesDataPageSearchBox">
        <SearchBox
          onChange={setSearchTerm}
          placeholder={searchPlaceholder}
          value={searchTerm}
        />
      </div>
      <ProductFilterSelect
        containerStyle={{
          left: "0px",
          borderRadius: "8px",
        }}
        filterByParentCollections={false}
        showGeneralStatusSection
        showManageTagsButton
        setFilters={(
          filterToSelectedCollectionIds: Array<number>,
          filterToRequiresClientAttention: boolean,
          intersectCollections: boolean,
          filterToSelectedGhgImpactRatings: Array<string>,
          filterToSelectedLandUseImpactRatings: Array<string>,
          filterToSelectedWaterUseImpactRatings: Array<string>,
          filterToSelectedDiets: Array<string>,
          filterToSelectedIntegrationTypes: Array<IntegrationType>
        ) =>
          onChange({
            filterToSelectedCollectionIds,
            filterToRequiresClientAttention,
            intersectCollections,
            filterToSelectedGhgImpactRatings,
            filterToSelectedLandUseImpactRatings,
            filterToSelectedWaterUseImpactRatings,
            filterToSelectedDiets,
            filterToSelectedIntegrationTypes,
          })
        }
        filters={{
          filterToSelectedCollectionIds:
            queryParams.filterToSelectedCollectionIds,
          filterToRequiresClientAttention:
            queryParams.filterToRequiresClientAttention,
          intersectCollections: queryParams.intersectCollections,
          filterToSelectedGhgImpactRatings:
            queryParams.filterToSelectedGhgImpactRatings,
          filterToSelectedLandUseImpactRatings:
            queryParams.filterToSelectedLandUseImpactRatings,
          filterToSelectedWaterUseImpactRatings:
            queryParams.filterToSelectedWaterUseImpactRatings,
          filterToSelectedDiets: queryParams.filterToSelectedDiets,
          filterToSelectedIntegrationTypes:
            queryParams.filterToSelectedIntegrationTypes,
        }}
        menuIsOpen={menuIsOpen}
        setMenuIsOpen={setMenuIsOpen}
        hideAttention
      />
    </div>
  );
}

export function SalesDataPageControlsRight() {
  const pages = usePages();
  const { impactCategory, setImpactCategory } = useImpactCategory();
  const availableImpactCategories = useAvailableImpactCategories();

  return (
    <>
      <div>
        <SecondaryButtonLink
          to={pages.SalesDataUpload.url}
          icon={<Upload width={16} />}
        >
          <FormattedMessage
            id="components/sales-data/SalesDataPage:uploadSalesData"
            defaultMessage="Upload"
          />
        </SecondaryButtonLink>
      </div>
      <div>
        <ImpactCategoryToggle
          onChange={setImpactCategory}
          options={availableImpactCategories}
          selectedImpactCategory={impactCategory}
        />
      </div>
    </>
  );
}

interface SalesDataTableWithStatusProps {
  combinedParams: string;
  selectedTimePeriod: SalesDataTimePeriod | null;
}

function SalesDataTableWithStatus(props: SalesDataTableWithStatusProps) {
  const { combinedParams, selectedTimePeriod } = props;
  const { impactCategory } = useImpactCategory();
  const [salesDataStatus] = useSalesData(combinedParams, selectedTimePeriod);

  return (
    <StatusDisplay status={salesDataStatus}>
      {(salesData) => {
        if (salesData.success === false) {
          if (salesData.error.code === "CACHED_IMPACT_NOT_FOUND") {
            return (
              <Panel>
                <div className="p-5 text-center">
                  <FormattedMessage
                    id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                    defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                  />
                </div>
              </Panel>
            );
          }
        } else {
          if (salesData.recipes.length === 0) {
            return (
              <Panel>
                <div className="p-5 text-center">
                  <FormattedMessage
                    id="components/sales-data/SalesDataPage:noSalesData"
                    defaultMessage="No sales data available for the selected time period."
                  />
                </div>
              </Panel>
            );
          }

          return (
            <SalesDataTable
              rows={salesData.recipes}
              impactCategory={impactCategory}
            />
          );
        }
      }}
    </StatusDisplay>
  );
}

interface SalesDataTableProps {
  rows: SalesDataItem[];
  impactCategory: ImpactCategory;
}

function SalesDataTable(props: SalesDataTableProps) {
  const { rows, impactCategory } = props;

  const intl = useIntl();
  const pages = usePages();
  const [sort, setSort] = useState<TableSort | null>({
    columnKey: "contribution",
    descending: true,
  });
  const [{ locale }] = useUserInfo();

  const impactColumnLabel = () => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:column/landUse",
          defaultMessage: "Land Use",
        });
      case ImpactCategory.WATER_USE:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:column/waterUse",
          defaultMessage: "Water Use",
        });
      case ImpactCategory.GHG:
      default:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:column/ghgEmissions",
          defaultMessage: "GHG Emissions",
        });
    }
  };

  const impactUnits = () => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:units/m2",
          defaultMessage: "m²",
        });
      case ImpactCategory.WATER_USE:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:units/L",
          defaultMessage: "L",
        });
      case ImpactCategory.GHG:
      default:
        return intl.formatMessage({
          id: "components/sales-data/SalesDataPage:units/tCO2e",
          defaultMessage: "t CO2e",
        });
    }
  };

  const getImpactValue = (item: SalesDataItem) => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return item.landUse;
      case ImpactCategory.WATER_USE:
        return item.waterUse;
      case ImpactCategory.GHG:
      default:
        return item.ghgEmissions;
    }
  };

  const getIntensityValue = (item: SalesDataItem) => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return item.landUseIntensity;
      case ImpactCategory.WATER_USE:
        return item.waterUseIntensity;
      case ImpactCategory.GHG:
      default:
        return item.ghgIntensity;
    }
  };

  const getContributionValue = (item: SalesDataItem) => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return item.landUseContribution;
      case ImpactCategory.WATER_USE:
        return item.waterUseContribution;
      case ImpactCategory.GHG:
      default:
        return item.ghgContribution;
    }
  };

  const getRatingValue = (item: SalesDataItem) => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return item.landUseRating;
      case ImpactCategory.WATER_USE:
        return item.waterUseRating;
      case ImpactCategory.GHG:
      default:
        return item.ghgRating;
    }
  };

  const intensityColumnLabel = () => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return effectTypes.landUsePerKg.title(intl);
      case ImpactCategory.WATER_USE:
        return effectTypes.waterUsePerKg.title(intl);
      case ImpactCategory.GHG:
      default:
        return effectTypes.ghgPerKg.title(intl);
    }
  };

  const intensityUnits = () => {
    switch (impactCategory) {
      case ImpactCategory.LAND_USE:
        return effectTypes.landUsePerKg.unit(intl);
      case ImpactCategory.WATER_USE:
        return effectTypes.waterUsePerKg.unit(intl);
      case ImpactCategory.GHG:
      default:
        return effectTypes.ghgPerKg.unit(intl);
    }
  };

  const columns: Array<TableColumn<SalesDataItem>> = [
    {
      key: "name",
      label: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:column/product",
        defaultMessage: "Product",
      }),
      className: "product-column",
      renderCell: (item) => (
        <Link to={pages.Recipe.url({ id: item.id })} className="product-link">
          {formatString(item.name)}
        </Link>
      ),
      sortComparator: comparators.map(
        (item) => item.name,
        comparators.stringSensitivityBase
      ),
      verticalAlign: "middle",
    },
    {
      key: "sales",
      label: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:column/sales",
        defaultMessage: "Sales",
      }),
      units: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:units/productsSold",
        defaultMessage: "products sold",
      }),
      renderCell: (item) => intlFormatNumber(intl, item.sales),
      sortComparator: comparators.map((item) => item.sales, comparators.number),
      verticalAlign: "middle",
    },
    {
      key: "impact",
      label: impactColumnLabel(),
      units: impactUnits(),
      renderCell: (item) => intlFormatNumber(intl, getImpactValue(item)),
      sortComparator: comparators.map(
        (item) => getImpactValue(item),
        comparators.number
      ),
      verticalAlign: "middle",
    },
    {
      key: "intensity",
      label: intensityColumnLabel(),
      units: intensityUnits(),
      renderCell: (item) => intlFormatNumber(intl, getIntensityValue(item)),
      sortComparator: comparators.map(
        (item) => getIntensityValue(item),
        comparators.number
      ),
      verticalAlign: "middle",
    },
    {
      key: "rating",
      label: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:column/rating",
        defaultMessage: "Rating",
      }),
      units: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:units/rating",
        defaultMessage: "A-E",
      }),
      renderCell: (item) => (
        <Label
          colourSetting="colour"
          type="letterRating"
          impactCategory={impactCategory}
          impactRating={getRatingValue(item)}
          locale={locale}
          width={46}
        />
      ),
      sortComparator: comparators.map(
        (item) => allImpactRatings.indexOf(getRatingValue(item)),
        comparators.number
      ),
      verticalAlign: "middle",
    },
    {
      key: "mass",
      label: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:column/mass",
        defaultMessage: "Mass",
      }),
      units: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:units/tSold",
        defaultMessage: "t sold",
      }),
      renderCell: (item) => intlFormatNumber(intl, item.mass),
      sortComparator: comparators.map(
        (item) => item.mass ?? 0,
        comparators.number
      ),
      verticalAlign: "middle",
    },
    {
      key: "contribution",
      label: intl.formatMessage({
        id: "components/sales-data/SalesDataPage:column/contribution",
        defaultMessage: "Contribution",
      }),
      units: (() => {
        switch (impactCategory) {
          case ImpactCategory.LAND_USE:
            return intl.formatMessage({
              id: "components/sales-data/SalesDataPage:units/percentTotalLandUse",
              defaultMessage: "% of total land use",
            });
          case ImpactCategory.WATER_USE:
            return intl.formatMessage({
              id: "components/sales-data/SalesDataPage:units/percentTotalWaterUse",
              defaultMessage: "% of total water use",
            });
          case ImpactCategory.GHG:
          default:
            return intl.formatMessage({
              id: "components/sales-data/SalesDataPage:units/percentTotalGHG",
              defaultMessage: "% of total GHG",
            });
        }
      })(),
      renderCell: (item) =>
        intl.formatNumber(getContributionValue(item) / 100, {
          style: "percent",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      sortComparator: comparators.map(
        (item) => getContributionValue(item),
        comparators.number
      ),
      verticalAlign: "middle",
    },
  ];

  return (
    <Table<SalesDataItem>
      fullWidth
      columns={columns}
      onSortChange={setSort}
      rowKey={(item) => item.id}
      rows={rows}
      sort={sort}
    />
  );
}
